import { memo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Check, Download, Eye, FileText, Loader2, Trash2, Upload } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/utils';
import { cleanMarkdownForDisplay } from '@/lib/docling-markdown';
import { toastMutationError } from '@/lib/mutationError';
import { Button } from '@/components/ui/button';
import { supportingDocumentDownloadUrl } from '@/features/product/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useDeleteSupportingDocument,
  useSupportingDocMarkdown,
  useSupportingDocumentLimits,
  useSupportingDocuments,
  useUploadSupportingDocuments,
} from '@/features/product/hooks';
import type { SupportingDocument } from '@/features/product/api';
import { describeBatchFailures, type UploadProgress } from '@/features/product/upload-batches';
import {
  SupportingDocsQuota,
  type QuotaRejection,
} from './SupportingDocsQuota';
import { approxLengthTitle } from '@/features/product/text-size';

/**
 * Free-form supporting-documents uploader for a product. Many files per product,
 * identified by filename. Accepts PDF / DOCX / XLSX / CSV (per-file cap is an admin dial). The
 * backend stores the file and (for non-PDF) a markdown conversion the dossier
 * agent can read — but that mechanic is hidden from the user: a file is either
 * fine (quiet check) or flagged as unreadable. Self-contained + keyed only by
 * ``productId`` so it can be reused anywhere (the edit page now; a future
 * wizard later).
 */

const ACCEPT =
  '.pdf,.docx,.xlsx,.csv,application/pdf,' +
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document,' +
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv';

export const SUPPORTING_DOC_MAX_BYTES = 10 * 1024 * 1024; // fallback until the server's dial loads
const ALLOWED_EXT = ['.pdf', '.docx', '.xlsx', '.csv'];

export function isAllowedSupportingFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return ALLOWED_EXT.some((ext) => name.endsWith(ext));
}

/** Whole megabytes, matching how the server phrases its limit messages. */
function mb(bytes: number): number {
  return Math.floor(bytes / (1024 * 1024));
}

export function SupportingDocumentsUploader({
  productId,
}: {
  productId: number | null;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<SupportingDocument | null>(null);
  // Survives past a toast: what the last selection could not take, and why.
  const [rejection, setRejection] = useState<QuotaRejection | null>(null);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const docsQuery = useSupportingDocuments(productId);
  const limitsQuery = useSupportingDocumentLimits(productId);
  const upload = useUploadSupportingDocuments(productId);
  const remove = useDeleteSupportingDocument(productId);
  // Server-owned so the numbers shown are the ones enforced (both are admin dials).
  // Until they load, fall back to the per-file constant and leave the quota line out
  // rather than printing a guess that a dialled environment would contradict.
  const limits = limitsQuery.data ?? null;
  const perFileMax = limits?.max_file_bytes ?? SUPPORTING_DOC_MAX_BYTES;

  const canUpload = productId != null && !upload.isPending;

  const handleFiles = async (fileList: FileList | null) => {
    if (!fileList || productId == null) return;
    const valid: File[] = [];
    for (const f of Array.from(fileList)) {
      if (!isAllowedSupportingFile(f)) {
        toast.error(`${f.name}: only PDF, DOCX, XLSX, CSV are allowed.`);
        continue;
      }
      if (f.size > perFileMax) {
        toast.error(`${f.name}: exceeds the ${mb(perFileMax)} MB limit.`);
        continue;
      }
      valid.push(f);
    }
    if (valid.length === 0) return;
    // Pre-flight against the product's remaining quota: upload what fits and keep the
    // rest on screen by name. The server enforces the same ceilings, but checking here
    // means the user learns BEFORE a long upload — the failure mode that sent dozens of
    // files up only to have the whole batch refused on arrival.
    let toUpload = valid;
    if (limits) {
      const room = Math.max(0, limits.max_files - limits.used_files);
      let total = limits.used_bytes;
      const accepted: File[] = [];
      const refused: string[] = [];
      let reason: QuotaRejection['reason'] = 'count';
      let stoppedAt: string | null = null;
      for (const f of valid) {
        if (accepted.length >= room) {
          if (stoppedAt === null) {
            reason = 'count';
            stoppedAt = f.name;
          }
          refused.push(f.name);
          continue;
        }
        if (total + f.size > limits.max_total_bytes) {
          if (stoppedAt === null) {
            reason = 'size';
            stoppedAt = f.name;
          }
          refused.push(f.name);
          continue;
        }
        total += f.size;
        accepted.push(f);
      }
      setRejection(refused.length > 0 ? { names: refused, reason, stoppedAt } : null);
      if (accepted.length === 0) return;
      toUpload = accepted;
    }
    try {
      const result = await upload.mutateAsync({ files: toUpload, onProgress: setProgress });
      if (result.uploaded.length > 0) {
        toast.success(
          `${result.uploaded.length} document${result.uploaded.length > 1 ? 's' : ''} uploaded.`,
        );
      }
      const why = describeBatchFailures(result.failed);
      if (why) toast.error(why);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      setProgress(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const openPicker = () => {
    if (canUpload) fileInputRef.current?.click();
  };

  const docs = docsQuery.data ?? [];

  return (
    <div>
      <p className="text-muted text-[13px] m-0 mb-3">
        Add documents and data files that support this product (PDF, DOCX, XLSX, CSV). They
        give the Regara AI more context to work with.
        {productId == null && (
          <span className="text-state-warning-text font-semibold">
            {' '}
            Create the product first, then add supporting documents here.
          </span>
        )}
      </p>

      <div
        role="button"
        tabIndex={canUpload ? 0 : -1}
        aria-disabled={!canUpload}
        onClick={openPicker}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openPicker();
          }
        }}
        onDragOver={(e) => {
          if (!canUpload) return;
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          if (!canUpload) return;
          e.preventDefault();
          setDragOver(false);
          void handleFiles(e.dataTransfer.files);
        }}
        className={cn(
          'flex flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed px-6 py-7 text-center transition-colors',
          canUpload ? 'cursor-pointer' : 'cursor-not-allowed opacity-60',
          dragOver
            ? 'border-electric bg-electric/5'
            : 'border-line hover:border-navy hover:bg-electric/[0.03]',
        )}
      >
        <Upload className="size-5 text-navy" aria-hidden />
        <div className="text-[13.5px] font-semibold text-navy">
          {upload.isPending
            ? progress && progress.total > 0
              ? `Uploading… ${progress.done} of ${progress.total}`
              : 'Uploading…'
            : 'Drag files here, or click to browse'}
        </div>
        <div className="text-muted text-[11.5px]">
          PDF, DOCX, XLSX, CSV · up to {mb(perFileMax)} MB each
        </div>

      </div>
      {limits && (
        <div className="mt-3">
          <SupportingDocsQuota
            usedFiles={limits.used_files}
            maxFiles={limits.max_files}
            usedBytes={limits.used_bytes}
            maxBytes={limits.max_total_bytes}
            textChars={limits.text_chars_total}
            textBudget={limits.agent_text_budget}
            rejection={rejection}
            onDismissRejection={() => setRejection(null)}
          />
        </div>
      )}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => void handleFiles(e.target.files)}
      />

      {docs.length > 0 && (
        // Same reason as the create screen: a full product's list would otherwise run
        // far past the drop zone.
        <ul
          className={cn(
            'mt-3 space-y-2',
            docs.length > 8 &&
              'max-h-[20rem] overflow-y-auto rounded-lg border border-line p-1.5',
          )}
        >
          {docs.map((d) => (
            <SupportingDocRow
              key={d.id}
              doc={d}
              // A refusal must say why — e.g. 409 "This product has been archived…" from a
              // stale tab. The global handler covers only network / 5xx.
              onRemove={() =>
                remove.mutate(d.id, {
                  onError: (e) => toastMutationError(e, "Couldn't remove the document."),
                })
              }
              removing={remove.isPending}
              onPreview={() => setPreviewDoc(d)}
              downloadUrl={
                productId != null ? supportingDocumentDownloadUrl(productId, d.id) : undefined
              }
            />
          ))}
        </ul>
      )}

      <SupportingDocPreviewDialog
        productId={productId}
        doc={previewDoc}
        onClose={() => setPreviewDoc(null)}
      />
    </div>
  );
}

/** One preview chunk, parsed ONCE. The document list re-renders every few seconds
 *  while other files are still converting; without memoisation every poll re-parsed
 *  ~100k characters of Markdown and the open preview stuttered. */
const MarkdownChunk = memo(function MarkdownChunk({ text }: { text: string }) {
  return <ReactMarkdown remarkPlugins={[remarkGfm]}>{cleanMarkdownForDisplay(text)}</ReactMarkdown>;
});

function SupportingDocPreviewDialog({
  productId,
  doc,
  onClose,
}: {
  productId: number | null;
  doc: SupportingDocument | null;
  onClose: () => void;
}) {
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useSupportingDocMarkdown(productId, doc?.id ?? null);
  const pages = data?.pages ?? [];
  const first = pages[0];
  const loadedChars = pages.reduce((n, p) => n + (p.markdown?.length ?? 0), 0);
  const total = first?.total_chars ?? null;
  return (
    <Dialog open={doc != null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex h-[80vh] w-[95vw] flex-col sm:max-w-5xl">
        <DialogHeader className="shrink-0">
          <DialogTitle className="text-[15px]">
            What the AI will read from {doc?.filename}
          </DialogTitle>
        </DialogHeader>
        <p className="text-muted text-[12px] -mt-1 shrink-0">
          This is the text the agent reads. Check that it matches your document. If it
          is garbled, the file is likely a scan or has damaged text — replace it with a
          clearer copy, or remove it so the agent is not working from bad text.
        </p>
        <div className="flex-1 min-h-0 overflow-y-auto rounded-md border border-line bg-white p-4">
          {isLoading ? (
            <p className="text-muted text-[13px] m-0">Loading…</p>
          ) : first?.markdown == null ? (
            <p className="text-muted text-[13px] m-0">No preview available for this file.</p>
          ) : (
            // `prose` styles links for us here (unlike the spec panes, which are deliberately
            // not prose and had to restore link colour themselves), but it does NOT wrap long
            // table cells — so one long value (an ID, a hash) stretched its column past the
            // dialog. `anywhere` on the cells, same as the spec panes. Deliberately NOT
            // `display:block` + a scroller on the table: that is the usual horizontal-scroll
            // trick but it drops the table box, and column alignment is the point of a table.
            <div className="prose prose-slate prose-sm max-w-none prose-table:text-[12px] [&_td]:[overflow-wrap:anywhere] [&_th]:[overflow-wrap:anywhere]">
              {/* One Markdown instance per chunk: a long document arrives in parts the
                  user appends, so the browser never parses megabytes at once. */}
              {pages.map((p) => (
                <MarkdownChunk key={p.offset} text={p.markdown ?? ''} />
              ))}
              {hasNextPage && (
                <div className="not-prose mt-4 flex items-center gap-3 border-t border-line pt-3">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={isFetchingNextPage}
                    onClick={() => void fetchNextPage()}
                  >
                    {isFetchingNextPage ? 'Loading…' : 'Show more'}
                  </Button>
                  {total != null && (
                    <span className="text-muted text-[12px]">
                      Showing {Math.round((loadedChars / total) * 100)}% of the document
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SupportingDocRow({
  doc,
  onRemove,
  removing,
  onPreview,
  downloadUrl,
}: {
  doc: SupportingDocument;
  onRemove: () => void;
  removing: boolean;
  onPreview: () => void;
  downloadUrl?: string;
}) {
  const sizeMB = (doc.size_bytes / (1024 * 1024)).toFixed(2);
  const failed = doc.conversion_status.startsWith('failed');
  const tooLarge = doc.conversion_status === 'failed: too large for agents';
  // A large PDF is stored immediately and converted in the background.
  const converting = doc.conversion_status === 'pending';
  // Only converted files (DOCX/XLSX/CSV → markdown) have text to preview; PDFs
  // are read natively (nothing converted) and failures are already flagged.
  const previewable = doc.conversion_status === 'ok';
  return (
    <li className="flex items-center gap-2 bg-white border border-line rounded-md py-1.5 px-2 text-[12.5px]">
      <FileText className="size-3.5 text-muted shrink-0" aria-hidden />
      <span className="flex-1 min-w-0 truncate font-semibold text-navy">
        {doc.filename}
      </span>
      <span
        className="text-muted text-[11px] shrink-0 tabular-nums"
        title={doc.text_chars != null ? approxLengthTitle(doc.text_chars) : undefined}
      >
        {sizeMB} MB
      </span>
      {tooLarge ? (
        <span
          className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold py-0.5 px-1.5 rounded bg-state-error-bg text-state-error-text"
          title="This document holds more text than the agents can use with the others on this product. Remove it or shorten it."
        >
          <AlertTriangle className="size-3" aria-hidden /> Too large
        </span>
      ) : failed ? (
        <span
          className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold py-0.5 px-1.5 rounded bg-state-error-bg text-state-error-text"
          title="We couldn't read this file — try re-saving it as a PDF and uploading again."
        >
          <AlertTriangle className="size-3" aria-hidden /> Couldn&apos;t read
        </span>
      ) : converting ? (
        <span
          className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold py-0.5 px-1.5 rounded bg-state-info-bg text-muted"
          title="Stored. A large document is converted to text in the background; this updates on its own."
        >
          <Loader2 className="size-3 animate-spin" aria-hidden /> Converting…
        </span>
      ) : (
        <Check
          className="size-3.5 text-state-success-accent shrink-0"
          aria-label="Uploaded"
        />
      )}
      {previewable && (
        <button
          type="button"
          onClick={onPreview}
          title="Preview what the AI will read"
          className="inline-flex items-center justify-center size-6 rounded text-muted hover:text-navy hover:bg-[#fafbfd] shrink-0"
        >
          <Eye className="size-3.5" aria-hidden />
          <span className="sr-only">Preview {doc.filename}</span>
        </button>
      )}
      {downloadUrl && (
        <a
          href={downloadUrl}
          download={doc.filename}
          title="Download"
          className="inline-flex items-center justify-center size-6 rounded text-muted hover:text-navy hover:bg-[#fafbfd] shrink-0"
        >
          <Download className="size-3.5" aria-hidden />
          <span className="sr-only">Download {doc.filename}</span>
        </a>
      )}
      <button
        type="button"
        onClick={onRemove}
        disabled={removing}
        title="Remove"
        className="inline-flex items-center justify-center size-6 rounded text-muted hover:text-state-error-text hover:bg-[#fafbfd] shrink-0 disabled:opacity-50"
      >
        <Trash2 className="size-3.5" aria-hidden />
        <span className="sr-only">Remove {doc.filename}</span>
      </button>
    </li>
  );
}
